import React, { useState } from 'react';
import { View, Text, TouchableOpacity, FlatList, TextInput, StyleSheet } from 'react-native';
import { showConfirm } from '@/utils/common/alert';
import { FormSection } from '@/components';
import { ITaskFormValues, ISubtaskItem } from '@/types/admin/tasks';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';

interface TaskSubtasksSectionProps {
  formData: ITaskFormValues;
  errors: Record<string, string | undefined>;
  onFieldChange: (field: keyof ITaskFormValues, value: any) => void;
}

export const TaskSubtasksSection: React.FC<TaskSubtasksSectionProps> = ({
  formData,
  errors,
  onFieldChange,
}) => {
  const [newSubtaskText, setNewSubtaskText] = useState('');

  // Add new subtask
  const addSubtask = () => {
    if (!newSubtaskText.trim()) return;

    const newSubtask: ISubtaskItem = {
      id: Date.now(), // Temporary ID for new subtasks
      title: newSubtaskText.trim(),
      sortIndex: formData.subtasks.length,
      assignee: '',
      createdAt: new Date().toISOString(),
    };

    onFieldChange('subtasks', [...formData.subtasks, newSubtask]);
    setNewSubtaskText('');
  };

  // Update subtask
  const updateSubtask = (index: number, updates: Partial<ISubtaskItem>) => {
    const updatedSubtasks = formData.subtasks.map((subtask, i) => 
      i === index ? { ...subtask, ...updates } : subtask
    );
    onFieldChange('subtasks', updatedSubtasks);
  };

  // Remove subtask
  const removeSubtask = async (index: number) => {
    const confirmed = await showConfirm(
      'Remove Subtask',
      'Are you sure you want to remove this subtask?',
      'Remove',
      'Cancel'
    );
    
    if (!confirmed) return;
    
    const updatedSubtasks = formData.subtasks.filter((_, i) => i !== index);
    // Update sort indices
    const reindexedSubtasks = updatedSubtasks.map((subtask, i) => ({
      ...subtask,
      sortIndex: i,
    }));
    onFieldChange('subtasks', reindexedSubtasks);
  };

  // Toggle subtask completion
  const toggleSubtaskCompletion = (index: number) => {
    const subtask = formData.subtasks[index];
    const isCompleted = !!subtask.completedAt;
    
    updateSubtask(index, {
      completedAt: isCompleted ? null : new Date().toISOString(),
    });
  };

  // Handle key press for new subtask input
  const handleNewSubtaskKeyPress = (e: any) => {
    if (e.nativeEvent.key === 'Enter') {
      addSubtask();
    }
  };

  // Handle key press for subtask editing
  const handleSubtaskKeyPress = (e: any, index: number) => {
    if (e.nativeEvent.key === 'Enter') {
      // Move focus away from input
      e.target.blur();
    }
  };

  const renderSubtaskItem = ({ item, index }: { item: ISubtaskItem; index: number }) => {
    const isCompleted = !!item.completedAt;

    return (
      <View style={[styles.subtaskItem, isCompleted && styles.completedSubtaskItem]}>
        {/* Drag Handle */}
        <View style={styles.dragHandle}>
          <SVG_COMPONENTS.DRAG_ICON width={16} height={16} color={ADMIN_COLORS.TEXT_TERTIARY} />
        </View>

        {/* Completion Checkbox */}
        <TouchableOpacity
          style={[styles.checkbox, isCompleted && styles.completedCheckbox]}
          onPress={() => toggleSubtaskCompletion(index)}
        >
          {isCompleted && (
            <SVG_COMPONENTS.CHECK_CIRCLE width={16} height={16} color={ADMIN_COLORS.SUCCESS} />
          )}
        </TouchableOpacity>

        {/* Subtask Title Input */}
        <TextInput
          style={[
            styles.subtaskInput,
            isCompleted && styles.completedSubtaskInput,
          ]}
          value={item.title}
          onChangeText={(text) => updateSubtask(index, { title: text })}
          onKeyPress={(e) => handleSubtaskKeyPress(e, index)}
          placeholder="Subtask title"
          placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
          multiline={false}
          returnKeyType="done"
          blurOnSubmit
        />

        {/* Remove Button */}
        <TouchableOpacity
          style={styles.removeButton}
          onPress={() => removeSubtask(index)}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <SVG_COMPONENTS.X_CIRCLE width={16} height={16} color={ADMIN_COLORS.ERROR} />
        </TouchableOpacity>
      </View>
    );
  };

  const completedCount = formData.subtasks.filter(s => s.completedAt).length;
  const totalCount = formData.subtasks.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <FormSection 
      title="Subtasks" 
      subtitle={`Break down the task into smaller steps (${completedCount}/${totalCount} completed)`}
    >
      {/* Progress Bar */}
      {totalCount > 0 && (
        <View style={styles.progressContainer}>
          <View style={styles.progressBar}>
            <View 
              style={[
                styles.progressFill, 
                { width: `${progressPercentage}%` }
              ]} 
            />
          </View>
          <Text style={styles.progressText}>{Math.round(progressPercentage)}%</Text>
        </View>
      )}

      {/* Subtasks List */}
      {formData.subtasks.length > 0 && (
        <FlatList
          data={formData.subtasks}
          renderItem={renderSubtaskItem}
          keyExtractor={(item, index) => `${item.id}-${index}`}
          style={styles.subtasksList}
          scrollEnabled={false} // Disable scroll since we're in a ScrollView
          showsVerticalScrollIndicator={false}
        />
      )}

      {/* Add New Subtask */}
      <View style={styles.addSubtaskContainer}>
        <View style={styles.addSubtaskInputContainer}>
          <TextInput
            style={styles.addSubtaskInput}
            value={newSubtaskText}
            onChangeText={setNewSubtaskText}
            onSubmitEditing={addSubtask}
            onKeyPress={handleNewSubtaskKeyPress}
            placeholder="Type subtask & press enter to add..."
            placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
            multiline={false}
            returnKeyType="done"
            blurOnSubmit
          />
          {newSubtaskText.trim().length > 0 && (
            <TouchableOpacity
              style={styles.addButton}
              onPress={addSubtask}
            >
              <SVG_COMPONENTS.ADD_BUTTON width={24} height={24} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {errors.subtasks && <Text style={styles.errorText}>{errors.subtasks}</Text>}

      {/* Subtasks Info */}
      <View style={styles.infoContainer}>
        <Text style={styles.infoText}>
          • Press Enter or tap + to add a subtask{'\n'}
          • Tap the checkbox to mark as complete{'\n'}
          • Use the drag handle to reorder (coming soon){'\n'}
          • Maximum 20 subtasks allowed
        </Text>
      </View>
    </FormSection>
  );
};

const styles = StyleSheet.create({
  progressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  progressBar: {
    flex: 1,
    height: 8,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: ADMIN_COLORS.SUCCESS,
    borderRadius: 4,
  },
  progressText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
    minWidth: 40,
    textAlign: 'right',
  },
  subtasksList: {
    marginBottom: 16,
  },
  subtaskItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    marginBottom: 8,
  },
  completedSubtaskItem: {
    opacity: 0.7,
  },
  dragHandle: {
    padding: 4,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
  },
  completedCheckbox: {
    borderColor: ADMIN_COLORS.SUCCESS,
    backgroundColor: ADMIN_COLORS.SUCCESS + '20',
  },
  subtaskInput: {
    flex: 1,
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    backgroundColor: 'transparent',
    paddingVertical: 4,
    paddingHorizontal: 0,
  },
  completedSubtaskInput: {
    textDecorationLine: 'line-through',
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  removeButton: {
    padding: 4,
  },
  addSubtaskContainer: {
    marginBottom: 16,
  },
  addSubtaskInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderStyle: 'dashed',
  },
  addSubtaskInput: {
    flex: 1,
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    backgroundColor: 'transparent',
    paddingVertical: 4,
    paddingHorizontal: 0,
    fontStyle: 'italic',
  },
  addButton: {
    padding: 4,
  },
  errorText: {
    fontSize: 14,
    color: ADMIN_COLORS.ERROR,
    marginBottom: 8,
  },
  infoContainer: {
    padding: 12,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY + '80',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  infoText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    lineHeight: 16,
  },
});
